package com.cybergstudios.rtc.engine

/**
 * The engine is the core of the game. Its main functions are to bootstrap the game,
 * receive user input, render the game on a game loop, and maintain the game state.
 *
 * The engine is platform-agnostic and Context instances provide platform
 * specific implementations.
 */
data class Engine(val state: GameState) {

    /** The main game loop. */
    fun loop() {
        state.context.scheduleNextFrame(this)
        val tick = state.context.getTick()
        val delta = tick - state.tick
        if (delta <= 0) { return }

        when (state.mode) {
            GameStateMode.PLAYING -> {
                state.tick = tick
                integrate(delta)
                detectCollision()
                render()
                state.processOpponent()
            }
            GameStateMode.WON -> switchToTheNextLevelIfItExistsOrGameWonState()
            GameStateMode.WON_GAME -> renderGameWin()
            GameStateMode.LOST -> renderGameLost()
        }
    }

    fun moveRight(): Unit = state.paddlePlayer.moveRight()

    fun moveLeft(): Unit = state.paddlePlayer.moveLeft()

    private fun integrate(delta: Double) {
        if (delta <= 0) return;
        state.ball.integrate(delta);
        state.paddlePlayer.integrate(delta);
        state.paddleOpponent.integrate(delta);
    }

    private fun detectCollision() {
        CollisionDetection.processPaddles(state)
        CollisionDetection.processBall(state)
        CollisionDetection.processBallAndPaddles(state)
        CollisionDetection.processBallAndBlocks(state)
    }

    private fun render() {
        state.context.clearCanvas()
        state.context.renderGrid(state)
        state.context.renderPaddlePlayer(state)
        state.context.renderPaddleOpponent(state)
        state.context.renderBall(state)
    }

    private fun switchToTheNextLevelIfItExistsOrGameWonState() {
        val nextLevel = state.level.nextLevel()
        if (nextLevel != null) {
            state.applyNextLevel(nextLevel)
        } else {
            state.setThePlayerAsTheWinnerOfTheGame()
        }
    }

    private fun renderGameWin() = state.context.renderGameWin(state)

    private fun renderGameLost() = state.context.renderGameLost(state)
}
